import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Gauge, Search, AlertCircle, ChevronDown, ChevronRight, Download,
  Loader2, ExternalLink, TrendingUp, Clock, Sparkles, Wallet, DollarSign, Mail,
} from "lucide-react";
import { API, useAuth } from "@/contexts/AuthContext";

const CAT_STYLE = {
  core:     { label: "Core",       chip: "bg-[#FCEADB] text-[#A0521A]" },
  capacity: { label: "Capacity",   chip: "bg-[#DDE7E9] text-[#284952]" },
  capital:  { label: "Capital",    chip: "bg-[#FCE7EB] text-[#A12545]" },
};

const fmt = (n) => Number(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function statusStyle(pct, orphan) {
  if (orphan) return { text: "text-[#A0521A]", ring: "#EA823A" };
  if (pct >= 100) return { text: "text-red-600", ring: "#DC2626" };
  if (pct >= 90)  return { text: "text-red-500", ring: "#DC2626" };
  if (pct >= 75)  return { text: "text-[#A0521A]", ring: "#EA823A" };
  return { text: "text-[#1F6B3A]", ring: "#1F6B3A" };
}

export default function Utilisation() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState({});
  const [filter, setFilter] = useState("all"); // all | over | ending | orphan
  const [editing, setEditing] = useState(null); // {clientId, itemId, value}
  const [saving, setSaving] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/utilisation`);
      setData(data);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't load utilisation data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const clientHasIssue = useCallback((c, kind) => {
    if (kind === "over")    return c.line_items.some((li) => li.percent_used >= 90 && li.approved > 0);
    if (kind === "ending")  return c.days_left != null && c.days_left >= 0 && c.days_left <= 30;
    if (kind === "orphan")  return c.line_items.some((li) => li.orphan);
    return true;
  }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    const query = q.trim().toLowerCase();
    return data.clients.filter((c) => {
      if (query) {
        const name = `${c.first_name} ${c.last_name}`.toLowerCase();
        if (!name.includes(query)) return false;
      }
      if (filter !== "all" && !clientHasIssue(c, filter)) return false;
      return true;
    });
  }, [data, q, filter, clientHasIssue]);

  const toggle = (id) => setExpanded((s) => ({ ...s, [id]: !s[id] }));
  const expandAll = () => {
    if (!data) return;
    const all = {}; filtered.forEach((c) => { all[c.id] = true; });
    setExpanded(all);
  };
  const collapseAll = () => setExpanded({});

  const saveEdit = async (clientId, itemId, value) => {
    setSaving(true);
    try {
      await axios.put(`${API}/clients/${clientId}/budget/items/${itemId}`, { approved_amount: Number(value || 0) });
      await load();
      setEditing(null);
    } catch (e) {
      alert(e?.response?.data?.detail || "Couldn't save.");
    } finally {
      setSaving(false);
    }
  };

  const sendEmailNow = async () => {
    if (!window.confirm("Send the weekly utilisation email to all admins right now?")) return;
    setSendingEmail(true);
    setEmailSent(false);
    try {
      await axios.post(`${API}/utilisation/email-now`);
      setEmailSent(true);
      setTimeout(() => setEmailSent(false), 5000);
    } catch (e) {
      alert(e?.response?.data?.detail || "Couldn't send the email.");
    } finally {
      setSendingEmail(false);
    }
  };

  const exportCsv = () => {
    if (!data) return;
    const rows = [["Client", "Category", "Sub-category", "Approved", "Spent", "Allocated", "Remaining", "% used", "Plan end", "Days left"]];
    data.clients.forEach((c) => {
      const name = `${c.first_name} ${c.last_name}`.trim();
      c.line_items.forEach((li) => {
        rows.push([
          name, CAT_STYLE[li.category]?.label || li.category, li.sub_category,
          li.approved.toFixed(2), li.spent.toFixed(2), li.allocated.toFixed(2),
          li.remaining.toFixed(2), `${li.percent_used}%`,
          c.plan_end_date || "", c.days_left ?? "",
        ]);
      });
    });
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `navica-utilisation-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="text-center py-20"><Loader2 className="w-8 h-8 mx-auto animate-spin text-[#E2718A]" /></div>;
  if (error) return <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-red-700 flex gap-3 items-start"><AlertCircle className="w-5 h-5 shrink-0 mt-0.5" /><div>{error}</div></div>;
  if (!data) return null;

  return (
    <div data-testid="utilisation-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="text-sm uppercase tracking-wider text-[#EA823A] font-semibold mb-2 flex items-center gap-2">
            <Gauge className="w-4 h-4" /> Utilisation
          </div>
          <h1 className="font-display text-4xl text-[#1F2A2E]">Every NDIS client, every category.</h1>
          <p className="text-[#4A595E] mt-2 max-w-2xl">Live view of approved, spent, allocated and remaining across every plan. Categories over 90% used or plans ending in 30 days bubble to the top.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isAdmin && (
            <button onClick={sendEmailNow} disabled={sendingEmail} className="btn-outline text-sm" data-testid="util-send-email-btn">
              {sendingEmail ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : emailSent ? <><Mail className="w-4 h-4" /> Sent!</> : <><Mail className="w-4 h-4" /> Send weekly email now</>}
            </button>
          )}
          <button onClick={exportCsv} className="btn-outline text-sm" data-testid="util-export-btn">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>
      </div>

      {/* Grand totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat label="Total approved"  value={`$${fmt(data.grand_total.approved)}`}  hint={`${data.clients.length} NDIS clients`} tone="navy" testId="util-approved" />
        <Stat label="Total spent"     value={`$${fmt(data.grand_total.spent)}`}     hint="Paid to date"     tone="green" testId="util-spent" />
        <Stat label="Total allocated" value={`$${fmt(data.grand_total.allocated)}`} hint="Invoiced / booked" tone="amber" testId="util-allocated" />
        <Stat label="Total remaining" value={`$${fmt(data.grand_total.remaining)}`} hint="Approved − spent − allocated" tone={data.grand_total.remaining < 0 ? "danger" : "coral"} testId="util-remaining" />
      </div>

      {/* Alerts strip */}
      <div className="grid sm:grid-cols-3 gap-3 mb-6">
        <AlertCard
          active={filter === "over"}
          onClick={() => setFilter(filter === "over" ? "all" : "over")}
          count={data.alerts.over_used}
          label="Categories over 90% used"
          hint="Money running out, top up or reallocate"
          icon={TrendingUp}
          testId="alert-over-used"
        />
        <AlertCard
          active={filter === "ending"}
          onClick={() => setFilter(filter === "ending" ? "all" : "ending")}
          count={data.alerts.plan_ending_soon}
          label="Plans ending in 30 days"
          hint="Time to plan for reviews & renewals"
          icon={Clock}
          testId="alert-ending"
        />
        <AlertCard
          active={filter === "orphan"}
          onClick={() => setFilter(filter === "orphan" ? "all" : "orphan")}
          count={data.alerts.orphan_spend}
          label="Orphan spend (no approved $)"
          hint="Statements imported spend for categories with no approved budget"
          icon={AlertCircle}
          testId="alert-orphan"
        />
      </div>

      {/* Search + actions */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#6B7A80]" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by client name…"
            className="w-full pl-10 pr-4 py-2.5 rounded-full border border-[#F0E5DE] focus:border-[#E2718A] outline-none text-sm bg-white"
            data-testid="util-search"
          />
        </div>
        <div className="flex gap-2">
          <button onClick={expandAll} className="text-xs font-semibold text-[#284952] hover:text-[#E2718A] px-3 py-2">Expand all</button>
          <button onClick={collapseAll} className="text-xs font-semibold text-[#284952] hover:text-[#E2718A] px-3 py-2">Collapse all</button>
        </div>
        {filter !== "all" && (
          <button onClick={() => setFilter("all")} className="text-xs font-semibold text-[#E2718A]">Clear filter</button>
        )}
      </div>

      {/* Client cards */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-3xl border border-[#F0E5DE]" data-testid="util-empty">
          <Wallet className="w-12 h-12 mx-auto text-[#F4C3CC] mb-4" />
          <h3 className="font-display text-xl text-[#1F2A2E] mb-2">
            {data.clients.length === 0 ? "No NDIS clients yet" : "No matches"}
          </h3>
          <p className="text-[#6B7A80] text-sm">
            {data.clients.length === 0 ? "Add an NDIS client and their budget will appear here." : "Try clearing the search or filter."}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((c) => (
            <ClientRow
              key={c.id}
              client={c}
              expanded={!!expanded[c.id]}
              onToggle={() => toggle(c.id)}
              editing={editing}
              setEditing={setEditing}
              onSave={saveEdit}
              saving={saving}
            />
          ))}
        </div>
      )}
      {/* Weekly digest note */}
      {isAdmin && data.clients.length > 0 && (
        <div className="mt-6 rounded-2xl bg-[#F1E4D5] border border-[#F0E5DE] px-5 py-4 flex items-start gap-3" data-testid="util-weekly-note">
          <Mail className="w-5 h-5 text-[#E2718A] mt-0.5 shrink-0" />
          <div className="text-sm text-[#284952] leading-relaxed">
            <span className="font-semibold text-[#1F2A2E]">Weekly summary emails are on.</span> Every Monday at 7am Sydney time we send this snapshot to admins so you don&apos;t need to check the CRM. Hit <span className="font-semibold">Send weekly email now</span> above to preview one.
          </div>
        </div>
      )}
    </div>
  );
}

function ClientRow({ client: c, expanded, onToggle, editing, setEditing, onSave, saving }) {
  const s = statusStyle(c.overall_percent_used, false);
  const grouped = { capacity: [], core: [], capital: [] };
  c.line_items.forEach((li) => (grouped[li.category] || (grouped[li.category] = [])).push(li));
  const daysLeftBadge = c.days_left != null && c.days_left >= 0 && c.days_left <= 30
    ? <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-red-50 text-red-600">plan ends in {c.days_left}d</span>
    : null;

  return (
    <div className="bg-white rounded-3xl border border-[#F0E5DE] overflow-hidden" data-testid={`util-client-${c.id}`}>
      <button
        onClick={onToggle}
        className="w-full px-5 py-4 flex items-center gap-4 hover:bg-[#F1E4D5]/40 transition text-left"
        data-testid={`util-toggle-${c.id}`}
      >
        <div className="w-6 shrink-0">
          {expanded ? <ChevronDown className="w-5 h-5 text-[#6B7A80]" /> : <ChevronRight className="w-5 h-5 text-[#6B7A80]" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-semibold text-[#1F2A2E]">{c.first_name} {c.last_name}</div>
            {daysLeftBadge}
            {c.assigned_to_name && <span className="text-xs text-[#6B7A80]">· {c.assigned_to_name}</span>}
          </div>
          <div className="text-xs text-[#6B7A80] mt-0.5">
            {c.plan_end_date ? `Plan ends ${c.plan_end_date}` : "No plan dates set"}
            {c.percent_elapsed != null && ` · ${c.percent_elapsed}% elapsed`}
          </div>
        </div>
        <div className="hidden sm:grid grid-cols-4 gap-6 text-right shrink-0">
          <Metric label="Approved"  value={c.totals.approved} />
          <Metric label="Spent"     value={c.totals.spent} />
          <Metric label="Allocated" value={c.totals.allocated} />
          <Metric label="Remaining" value={c.totals.remaining} accent={c.totals.remaining < 0} />
        </div>
        <div className="shrink-0 w-16 sm:w-20 text-right">
          <div className={`font-display text-2xl ${s.text} tabular-nums`}>{Math.round(c.overall_percent_used)}%</div>
          <div className="text-[10px] uppercase tracking-wider text-[#6B7A80]">used</div>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-[#F0E5DE] bg-[#F1E4D5]/30" data-testid={`util-details-${c.id}`}>
          <div className="p-5 flex items-center justify-between text-xs flex-wrap gap-2">
            <div className="text-[#6B7A80]">Click any Approved amount to edit inline.</div>
            <Link to={`/staff/clients/${c.id}`} className="text-[#E2718A] hover:text-[#284952] font-semibold inline-flex items-center gap-1">
              Open full client profile <ExternalLink className="w-3 h-3" />
            </Link>
          </div>
          {["capacity", "core", "capital"].map((cat) => {
            const items = grouped[cat] || [];
            if (items.length === 0) return null;
            return (
              <div key={cat} className="border-t border-[#F0E5DE]">
                <div className="px-5 py-2 text-[10px] uppercase tracking-wider font-semibold text-[#6B7A80]">{CAT_STYLE[cat].label}</div>
                <ul className="divide-y divide-[#F0E5DE]">
                  {items.map((li) => (
                    <LineItemRow
                      key={li.id || `orphan-${li.sub_category}`}
                      item={li}
                      clientId={c.id}
                      editing={editing}
                      setEditing={setEditing}
                      onSave={onSave}
                      saving={saving}
                    />
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function LineItemRow({ item, clientId, editing, setEditing, onSave, saving }) {
  const s = statusStyle(item.percent_used, item.orphan);
  const isEditing = editing?.clientId === clientId && editing?.itemId === item.id;

  return (
    <li className="px-5 py-3 flex items-center gap-4 flex-wrap" data-testid={`util-line-${item.id || item.sub_category}`}>
      <div className="flex-1 min-w-[180px]">
        <div className="flex items-center gap-2">
          <span className="font-medium text-[#1F2A2E]">{item.sub_category}</span>
          {item.orphan && <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-[#FCEADB] text-[#A0521A]">needs approved $</span>}
        </div>
      </div>

      {/* Approved (inline-editable when we have an id) */}
      <div className="w-32 text-right">
        <div className="text-[10px] uppercase tracking-wider text-[#6B7A80]">Approved</div>
        {isEditing ? (
          <div className="flex items-center gap-1 justify-end">
            <span className="text-[#6B7A80]">$</span>
            <input
              type="number" step="0.01" min="0" autoFocus
              value={editing.value}
              onChange={(e) => setEditing({ ...editing, value: e.target.value })}
              className="w-24 px-2 py-1 text-sm rounded border border-[#E2718A] outline-none tabular-nums text-right"
              data-testid={`util-edit-input-${item.id}`}
              onKeyDown={(e) => {
                if (e.key === "Enter") onSave(clientId, item.id, editing.value);
                if (e.key === "Escape") setEditing(null);
              }}
            />
            <button onClick={() => onSave(clientId, item.id, editing.value)} disabled={saving} className="text-[#E2718A] hover:text-[#284952] px-1.5 py-1 text-xs font-semibold" data-testid={`util-edit-save-${item.id}`}>
              {saving ? "…" : "Save"}
            </button>
            <button onClick={() => setEditing(null)} className="text-[#6B7A80] hover:text-[#1F2A2E] px-1.5 py-1 text-xs">Cancel</button>
          </div>
        ) : (
          <button
            onClick={() => item.id ? setEditing({ clientId, itemId: item.id, value: item.approved || 0 }) : null}
            disabled={!item.id}
            className={`font-semibold tabular-nums ${item.id ? "text-[#1F2A2E] hover:text-[#E2718A] cursor-pointer" : "text-[#6B7A80] cursor-default"}`}
            data-testid={`util-edit-approved-${item.id || "orphan"}`}
            title={item.id ? "Click to edit" : "Add this line to the budget on the client profile first"}
          >
            ${fmt(item.approved)}
          </button>
        )}
      </div>

      <Cell label="Spent"     value={item.spent} />
      <Cell label="Allocated" value={item.allocated} />
      <Cell label="Remaining" value={item.remaining} accent={item.remaining < 0} />

      <MiniBar percent={item.percent_used} tone={s.ring} />
    </li>
  );
}

function Stat({ label, value, hint, tone = "coral", testId }) {
  const tones = {
    navy:   "bg-[#DDE7E9] border-[#B8CACD] text-[#284952]",
    green:  "bg-[#DCEFE0] border-[#B9DDBF] text-[#1F6B3A]",
    amber:  "bg-[#FCEADB] border-[#F4CFA7] text-[#A0521A]",
    coral:  "bg-[#FCE7EB] border-[#F4C3CC] text-[#A12545]",
    danger: "bg-red-50 border-red-200 text-red-700",
  };
  return (
    <div className={`rounded-2xl p-4 border ${tones[tone]}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider font-semibold opacity-80 mb-1">{label}</div>
      <div className="font-display text-2xl text-[#1F2A2E] tabular-nums">{value}</div>
      <div className="text-xs opacity-80 mt-0.5">{hint}</div>
    </div>
  );
}

function AlertCard({ active, onClick, count, label, hint, icon: Icon, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`text-left rounded-2xl p-4 border transition ${
        active
          ? "bg-[#E2718A] border-[#E2718A] text-white"
          : count > 0
            ? "bg-white border-[#F4C3CC] hover:border-[#E2718A]"
            : "bg-white border-[#F0E5DE] opacity-70 hover:opacity-100"
      }`}
    >
      <div className="flex items-center gap-2 mb-1">
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${active ? "bg-white/20" : "bg-[#F1E4D5] text-[#E2718A]"}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className={`font-display text-2xl tabular-nums ${active ? "" : count > 0 ? "text-[#E2718A]" : "text-[#6B7A80]"}`}>{count}</div>
      </div>
      <div className={`text-sm font-semibold ${active ? "" : "text-[#1F2A2E]"}`}>{label}</div>
      <div className={`text-xs mt-0.5 ${active ? "text-white/80" : "text-[#6B7A80]"}`}>{hint}</div>
    </button>
  );
}

function Metric({ label, value, accent }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#6B7A80]">{label}</div>
      <div className={`font-semibold tabular-nums ${accent ? "text-red-600" : "text-[#1F2A2E]"}`}>${fmt(value)}</div>
    </div>
  );
}

function Cell({ label, value, accent }) {
  return (
    <div className="w-24 text-right">
      <div className="text-[10px] uppercase tracking-wider text-[#6B7A80]">{label}</div>
      <div className={`font-semibold tabular-nums ${accent ? "text-red-600" : "text-[#1F2A2E]"}`}>${fmt(value)}</div>
    </div>
  );
}

function MiniBar({ percent, tone }) {
  const w = Math.min(100, Math.max(0, percent || 0));
  return (
    <div className="w-24 shrink-0">
      <div className="h-2 rounded-full bg-[#F0E5DE] overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${w}%`, background: tone }} />
      </div>
      <div className="text-[10px] text-right text-[#6B7A80] mt-1 tabular-nums">{Math.round(w)}%</div>
    </div>
  );
}
